'use client';
import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { CheckCircle, Shield, Star, Award } from 'lucide-react';

const trustBadges = [
  { icon: CheckCircle, label: 'ID Verified' },
  { icon: Shield, label: 'POPIA Compliant' },
  { icon: Star, label: 'Work-Readiness Assessed' },
  { icon: Award, label: 'Reference Checked' },
];

const stats = [
  { value: '500+', label: 'Candidates Screened' },
  { value: 'R1,000', label: 'Per Profile Access' },
  { value: '5', label: 'Assessment Dimensions' },
  { value: '100%', label: 'SA Owned' },
];

export default function HomeHero() {
  return (
    <section className="relative overflow-hidden" style={{ background: 'var(--amber)' }}>
      {/* Subtle decorative circle */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-10 pointer-events-none" style={{ background: 'var(--charcoal)', transform: 'translate(30%, -30%)' }} />
      <div className="relative max-w-screen-xl mx-auto px-4 lg:px-8 pt-14 lg:pt-20 pb-0">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-end">
          {/* Left: Copy */}
          <div className="pb-14 lg:pb-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 text-xs font-bold uppercase tracking-wider" style={{ background: 'var(--charcoal)', color: 'var(--amber)' }}>
              South Africa&apos;s Work-Readiness Platform
            </div>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold leading-[1.1] mb-6" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>
              Where Work-Ready Talent Meets Opportunity
            </h1>
            <p className="text-lg mb-3 max-w-xl leading-relaxed font-medium" style={{ color: 'var(--charcoal)', opacity: 0.8 }}>
              Helping South Africans prove their work readiness and helping employers hire with confidence.
            </p>
            <p className="text-sm mb-8 max-w-lg italic font-semibold" style={{ color: 'var(--charcoal)', opacity: 0.65 }}>
              Attitude First. Opportunity Follows.
            </p>

            <div className="flex flex-wrap gap-3 mb-10">
              <Link
                href="/directory"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-bold text-base transition-all duration-150 active:scale-95"
                style={{ background: 'var(--charcoal)', color: 'white' }}
              >
                Find Candidates
              </Link>
              <Link
                href="/candidate"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-bold text-base border-2 transition-all duration-150 active:scale-95"
                style={{ borderColor: 'var(--charcoal)', color: 'var(--charcoal)', background: 'transparent' }}
              >
                Become Verified
              </Link>
            </div>

            {/* Trust strip */}
            <div className="flex flex-wrap gap-4">
              {trustBadges?.map((item) => (
                <div key={item?.label} className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--charcoal)', opacity: 0.7 }}>
                  <item.icon size={14} style={{ color: 'var(--charcoal)' }} />
                  {item?.label}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Illustration */}
          <div className="flex items-end justify-center lg:justify-end">
            <div className="relative w-full max-w-[480px]">
              <Image
                src="/assets/hero-handshake-illustration.svg"
                alt="Three people connecting — a handshake symbolising talent meeting opportunity, with a lightbulb representing ideas and growth"
                width={480}
                height={420}
                className="w-full h-auto"
                priority
              />
            </div>
          </div>
        </div>

        {/* Stats strip — sits at the bottom of the hero */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 pb-10 pt-2 border-t border-black/10 mt-4">
          {stats?.map((stat) => (
            <div key={stat?.label} className="py-4 text-center">
              <div className="text-2xl lg:text-3xl font-bold" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>{stat?.value}</div>
              <div className="text-xs font-medium mt-1" style={{ color: 'var(--charcoal)', opacity: 0.65 }}>{stat?.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}